import React, { useState, useEffect, useCallback } from "react";
import API from "../../config/api";
import { useAuth } from "../../hooks/useAuth";

const SENTIMENTS = ["", "Positive", "Neutral", "Negative"];
const DIRECTIONS = ["", "inbound", "outbound"];

export default function CallLogsPage() {
  const { getToken } = useAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState("");
  const [stats, setStats] = useState({});
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [expanded, setExpanded] = useState(null);

  // Filters
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [caller, setCaller] = useState("");
  const [sentiment, setSentiment] = useState("");
  const [direction, setDirection] = useState("");
  const [search, setSearch] = useState("");

  const headers = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` });

  const loadLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, per_page: 20 });
      if (dateFrom) params.set("date_from", Math.floor(new Date(dateFrom).getTime() / 1000));
      if (dateTo) params.set("date_to", Math.floor(new Date(dateTo + "T23:59:59").getTime() / 1000));
      if (caller) params.set("caller", caller);
      if (sentiment) params.set("sentiment", sentiment);
      if (direction) params.set("direction", direction);
      if (search) params.set("search", search);
      const res = await fetch(`${API.callLogsList}?${params}`, { headers: headers() });
      const data = await res.json();
      setLogs(data.logs || []);
      setTotal(data.total || 0);
      setPage(p);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, caller, sentiment, direction, search]); // eslint-disable-line

  useEffect(() => { loadLogs(1); }, [loadLogs]);

  const loadInsights = async () => {
    setInsightsLoading(true); setShowInsights(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", Math.floor(new Date(dateFrom).getTime() / 1000));
      if (dateTo) params.set("date_to", Math.floor(new Date(dateTo + "T23:59:59").getTime() / 1000));
      const res = await fetch(`${API.callLogsInsights}?${params}`, { headers: headers() });
      const data = await res.json();
      setInsights(data.insights || "No insights available");
      setStats(data.stats || {});
    } catch (e) { setInsights("Failed to load insights"); }
    finally { setInsightsLoading(false); }
  };

  const downloadCSV = () => {
    const csvHeaders = ["Time", "Caller", "Direction", "Duration", "Summary", "Sentiment", "Lead Score", "Is Lead", "Language", "Topics"];
    const csvRows = logs.map(l => [
      new Date(l.started_at * 1000).toLocaleString("en-IN"),
      l.caller_number, l.direction, l.duration_str, `"${(l.summary || "").replace(/"/g, '""')}"`,
      l.sentiment, l.lead_score, l.is_lead ? "Yes" : "No", l.language, `"${(l.topics || "").replace(/"/g, '""')}"`
    ]);
    const csv = [csvHeaders.join(","), ...csvRows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `call-logs-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const sentimentColor = s => ({ Positive: "#3fb950", Negative: "#f85149" }[s] || "#8b949e");
  const leadColor = s => ({ Hot: "#f85149", Warm: "#FF9900" }[s] || "#8b949e");
  const totalPages = Math.ceil(total / 20);

  return (
    <div style={S.page}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={S.title}>📞 Call Logs</h2>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={S.actionBtn} onClick={loadInsights}>🤖 AI Insights</button>
          <button style={{ ...S.actionBtn, background: "#238636" }} onClick={downloadCSV}>📥 CSV</button>
        </div>
      </div>
      <p style={S.sub}>View conversation history, sentiment analysis, and lead scoring for all calls.</p>

      {/* Filters */}
      <div style={S.filterBar}>
        <input type="date" style={S.filterInput} value={dateFrom} onChange={e => setDateFrom(e.target.value)} title="From date" />
        <input type="date" style={S.filterInput} value={dateTo} onChange={e => setDateTo(e.target.value)} title="To date" />
        <input style={{ ...S.filterInput, width: 140 }} placeholder="🔍 Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <input style={{ ...S.filterInput, width: 130 }} placeholder="Caller number" value={caller} onChange={e => setCaller(e.target.value)} />
        <select style={S.filterInput} value={sentiment} onChange={e => setSentiment(e.target.value)}>
          <option value="">All Sentiments</option>
          {SENTIMENTS.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select style={S.filterInput} value={direction} onChange={e => setDirection(e.target.value)}>
          <option value="">All Directions</option>
          {DIRECTIONS.filter(Boolean).map(d => <option key={d} value={d}>{d}</option>)}
        </select>
        <button style={{ ...S.actionBtn, padding: "6px 12px", fontSize: 12 }} onClick={() => { setDateFrom(""); setDateTo(""); setCaller(""); setSentiment(""); setDirection(""); setSearch(""); }}>
          ✕ Clear
        </button>
      </div>

      {/* AI Insights Panel */}
      {showInsights && (
        <div style={S.insightsPanel}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ color: "#e6edf3", margin: 0, fontSize: 15 }}>🤖 AI Insights</h3>
            <button style={{ background: "transparent", border: "none", color: "#8b949e", cursor: "pointer" }} onClick={() => setShowInsights(false)}>✕</button>
          </div>
          {insightsLoading ? <p style={{ color: "#8b949e", fontSize: 13 }}>Analyzing calls...</p> : (
            <>
              {stats.total_calls > 0 && (
                <div style={{ display: "flex", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
                  <Stat label="Total" value={stats.total_calls} color="#58a6ff" />
                  <Stat label="Positive" value={stats.sentiments?.Positive || 0} color="#3fb950" />
                  <Stat label="Negative" value={stats.sentiments?.Negative || 0} color="#f85149" />
                  <Stat label="Leads" value={stats.lead_count || 0} color="#FF9900" />
                  <Stat label="Lead Rate" value={`${stats.lead_rate || 0}%`} color="#FF9900" />
                  <Stat label="Avg Duration" value={`${stats.avg_duration || 0}s`} color="#e6edf3" />
                </div>
              )}
              <div style={{ color: "#c9d1d9", fontSize: 13, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{insights}</div>
            </>
          )}
        </div>
      )}

      {/* Table */}
      <div style={{ overflowX: "auto" }}>
        <table style={S.table}>
          <thead>
            <tr style={{ background: "#21262d" }}>
              {["Time", "Caller", "Dir", "Duration", "Summary", "Sentiment", "Lead", "Topics"].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: "#8b949e" }}>Loading...</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={8} style={{ ...S.td, textAlign: "center", color: "#8b949e" }}>No call logs found</td></tr>
            ) : logs.map((l, i) => (
              <React.Fragment key={l.call_sid}>
                <tr style={{ background: i % 2 === 0 ? "#161b22" : "#0d1117", cursor: "pointer" }}
                    onClick={() => setExpanded(expanded === l.call_sid ? null : l.call_sid)}>
                  <td style={S.td}>{new Date(l.started_at * 1000).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                  <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12 }}>{l.caller_number}</td>
                  <td style={S.td}><span style={{ ...S.badge, background: l.direction === "inbound" ? "#1f6feb33" : "#23863633", color: l.direction === "inbound" ? "#58a6ff" : "#3fb950" }}>{l.direction}</span></td>
                  <td style={S.td}>{l.duration_str}</td>
                  <td style={{ ...S.td, maxWidth: 280, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.summary}</td>
                  <td style={S.td}><span style={{ color: sentimentColor(l.sentiment), fontWeight: "bold" }}>{l.sentiment}</span></td>
                  <td style={S.td}>
                    <span style={{ color: leadColor(l.lead_score), fontWeight: "bold" }}>{l.lead_score}</span>
                    {l.is_lead && <span style={{ marginLeft: 4 }}>✅</span>}
                  </td>
                  <td style={{ ...S.td, color: "#8b949e", fontSize: 11 }}>{l.topics}</td>
                </tr>
                {expanded === l.call_sid && (
                  <tr style={{ background: "#0d1117" }}>
                    <td colSpan={8} style={{ padding: "12px 16px" }}>
                      <div style={{ color: "#c9d1d9", fontSize: 13, lineHeight: 1.6 }}>
                        <b style={{ color: "#58a6ff" }}>Full Summary:</b> {l.summary}<br/>
                        <b style={{ color: "#58a6ff" }}>Topics:</b> {l.topics}<br/>
                        <b style={{ color: "#58a6ff" }}>Language:</b> {l.language} &nbsp;|&nbsp;
                        <b style={{ color: "#58a6ff" }}>Called:</b> {l.called_number} &nbsp;|&nbsp;
                        <b style={{ color: "#58a6ff" }}>Status:</b> {l.status}
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
          <button style={S.pageBtn} disabled={page <= 1} onClick={() => loadLogs(page - 1)}>← Prev</button>
          <span style={{ color: "#8b949e", fontSize: 13, padding: "6px 12px" }}>Page {page} of {totalPages} ({total} calls)</span>
          <button style={S.pageBtn} disabled={page >= totalPages} onClick={() => loadLogs(page + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, padding: "8px 14px", textAlign: "center", minWidth: 70 }}>
      <div style={{ color, fontSize: 20, fontWeight: "bold" }}>{value}</div>
      <div style={{ color: "#8b949e", fontSize: 10 }}>{label}</div>
    </div>
  );
}

const S = {
  page: { padding: "24px 0" },
  title: { color: "#e6edf3", marginBottom: 6 },
  sub: { color: "#8b949e", fontSize: 13, marginBottom: 16 },
  filterBar: { display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" },
  filterInput: { padding: "6px 10px", background: "#0d1117", border: "1px solid #30363d", borderRadius: 6, color: "#e6edf3", fontSize: 12 },
  actionBtn: { padding: "8px 14px", background: "#1f6feb", border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", fontSize: 12, fontWeight: "bold" },
  insightsPanel: { background: "#161b22", border: "1px solid #1f6feb44", borderRadius: 10, padding: 20, marginBottom: 16 },
  table: { width: "100%", borderCollapse: "collapse", border: "1px solid #30363d", borderRadius: 8 },
  th: { padding: "10px 12px", color: "#8b949e", fontSize: 11, textAlign: "left", fontWeight: "600", textTransform: "uppercase" },
  td: { padding: "10px 12px", color: "#e6edf3", fontSize: 13, borderTop: "1px solid #21262d" },
  badge: { padding: "2px 8px", borderRadius: 4, fontSize: 11 },
  pageBtn: { padding: "6px 14px", background: "#21262d", border: "1px solid #30363d", borderRadius: 6, color: "#e6edf3", cursor: "pointer", fontSize: 12 },
};
